import { useNavigate, useLocation } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { requestMobile } from '@/lib/request'
import { request } from '@/lib/request'
import { esQuery } from '@/lib/es'
import {
  AlertTriangle,
  ArrowLeft,
  RefreshCw,
  Play,
  Square,
  Coins,
  TrendingUp,
  Pause,
} from 'lucide-react'
import { toast } from 'sonner'
import { RechargeDialog } from '@/components/account/RechargeDialog'
import { WithdrawDialog } from '@/components/account/WithdrawDialog'
import { BindCardDialog } from '@/components/account/BindCardDialog'
import { BindUsdtDialog } from '@/components/account/BindUsdtDialog'
import { ClaimRewardDialog } from '@/components/account/ClaimRewardDialog'
import { AuthSessionPanel } from '@/components/account/AuthSessionPanel'
import TaskTrendChart from '@/components/charts/TaskTrendChart'

interface AccountDetailState {
  remarks_name: string
  plat: string
  phone?: string
  total_account?: number
  task?: string
  task_id?: string
  node?: string
  account_id?: string
  level_integral?: number
  vip?: number
}

interface AccountDetailData {
  level_integral?: number
  vip?: number
  phone?: string
  total_account?: number
}

interface WithdrawRecord {
  order_no: string
  bank_name: string
  withdraw_money: number
  status: string
  withdraw_time: string
  remark?: string
}

interface RechargeRecord {
  des: string
  income: number
  cur: string
  status: string
  create_time: string
}

interface UsdtRechargeRecord {
  order_no: string
  bank_name: string | null
  color: string
  recharge_money: number
  recharge_time: string
  recharge_type: string | null
  remark: string
  status: number
}

interface ProxyItem {
  address?: string
  content?: string
  update_time: string
}

interface FlowItem {
  account: string
  balance: number
  cur: string
  bit: number
  current_pay: number
  code: string
  live_id: string
  pay_count: number
  current_count: number
  strategy: string
  date: string
  timestamp: number
}

export default function AccountDetail() {
  const navigate = useNavigate()
  const location = useLocation()
  const accountInfo = (location.state as AccountDetailState) || {} as AccountDetailState
  const [accountDetail, setAccountDetail] = useState<AccountDetailData>({})
  const [loading, setLoading] = useState(false)

  // 弹窗状态
  const [withdrawRecordsOpen, setWithdrawRecordsOpen] = useState(false)
  const [rechargeRecordsOpen, setRechargeRecordsOpen] = useState(false)
  const [usdtRechargeRecordsOpen, setUsdtRechargeRecordsOpen] = useState(false)
  const [withdrawRecords, setWithdrawRecords] = useState<WithdrawRecord[]>([])
  const [rechargeRecords, setRechargeRecords] = useState<RechargeRecord[]>([])
  const [usdtRechargeRecords, setUsdtRechargeRecords] = useState<UsdtRechargeRecord[]>([])
  const [usdtRechargeType, setUsdtRechargeType] = useState<10 | 1>(10) // 10: 线上, 1: 线下

  // IP列表
  const [proxyList, setProxyList] = useState<ProxyItem[]>([])

  // 各功能对话框状态
  const [rechargeDialogOpen, setRechargeDialogOpen] = useState(false)
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false)
  const [bindCardDialogOpen, setBindCardDialogOpen] = useState(false)
  const [bindUsdtDialogOpen, setBindUsdtDialogOpen] = useState(false)
  const [claimRewardDialogOpen, setClaimRewardDialogOpen] = useState(false)
  const [clearCodeConfirmOpen, setClearCodeConfirmOpen] = useState(false)
  const [clearingCode, setClearingCode] = useState(false)
  const [checkingWithdraw, setCheckingWithdraw] = useState(false)
  const [withdrawWarningOpen, setWithdrawWarningOpen] = useState(false)
  const [withdrawPendingIncome, setWithdrawPendingIncome] = useState(0)
  const [taskProcessRunning, setTaskProcessRunning] = useState(false)
  const [checkingTaskProcess, setCheckingTaskProcess] = useState(false)
  const [taskProcessChecked, setTaskProcessChecked] = useState(false)

  // 流程数据和任务日志
  const [flowData, setFlowData] = useState<FlowItem[]>([])
  const [taskLog, setTaskLog] = useState<string>('')
  const [loadingFlow, setLoadingFlow] = useState(false)
  const [loadingLog, setLoadingLog] = useState(false)
  const [timeRange, setTimeRange] = useState('1h')
  const [logLines, setLogLines] = useState('100')
  const taskLogViewportRef = useRef<HTMLDivElement>(null)

  const checkTaskProcess = async () => {
    if (!accountInfo.task) {
      setTaskProcessRunning(false)
      setTaskProcessChecked(true)
      return
    }

    setCheckingTaskProcess(true)
    try {
      const resp = await request<string[]>('/common/apis/v1/task_process', {})
      const processLines = Array.isArray(resp.data) ? resp.data : []
      const isRunning = processLines.some((line) => {
        const commandParts = line.trim().split(/\s+/)
        const taskScriptIndex = commandParts.findIndex((part) => part.endsWith('task.py'))
        return taskScriptIndex >= 0 && commandParts[taskScriptIndex + 1] === accountInfo.task
      })
      setTaskProcessRunning(isRunning)
    } catch (error) {
      console.error('Failed to check task process:', error)
    } finally {
      setCheckingTaskProcess(false)
      setTaskProcessChecked(true)
    }
  }

  const fetchAccountDetail = async () => {
    setLoading(true)
    try {
      const resp = await requestMobile('/common/apis/v1/detail', {
        plat: accountInfo.plat,
        name: accountInfo.remarks_name
      })
      if (resp && !resp.code) {
        setAccountDetail(resp.data)
      }
    } catch (error) {
      console.error('Failed to fetch account detail:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchProxyList = async () => {
    try {
      const resp = await requestMobile('/common/apis/v1/get_proxy_list', {
        name: accountInfo.remarks_name
      })
      if (resp && !resp.code) {
        setProxyList(resp.data.list || [])
      }
    } catch (error) {
      console.error('Failed to fetch proxy list:', error)
    }
  }

  const loadFlowData = async () => {
    if (!accountInfo?.remarks_name) return

    setLoadingFlow(true)
    try {
      const body = {
        query: {
          bool: {
            filter: [
              {
                term: {
                  'account.keyword': accountInfo.remarks_name,
                },
              },
            ],
            must: [
              {
                range: {
                  timestamp: {
                    gte: `now-${timeRange}`,
                    lte: 'now',
                  },
                },
              },
            ],
          },
        },
        sort: [{ timestamp: { order: 'desc' } }],
      }

      const result = await esQuery('xr_pay_new', body)
      if (result) {
        const rs = result.data ? result.data : result
        const list = rs.hits.hits.map((val: any) => val._source)
        setFlowData(list)
      }
    } catch (error) {
      console.error('加载流程数据失败:', error)
      toast.error('加载流程数据失败')
    } finally {
      setLoadingFlow(false)
    }
  }

  const loadTaskLog = async () => {
    if (!accountInfo?.task) return

    setLoadingLog(true)
    try {
      const requestBody: any = {
        t_name: accountInfo.task,
      }

      // 添加行数参数（如果用户输入了有效值）
      if (logLines && logLines.trim()) {
        requestBody.n = logLines.trim()
      }

      const resp = await requestMobile('/common/apis/v1/task_log', requestBody)
      if (resp && resp.code === 0) {
        setTaskLog(resp.data || '')
      }
    } catch (error) {
      console.error('加载任务日志失败:', error)
      toast.error('加载任务日志失败')
    } finally {
      setLoadingLog(false)
    }
  }

  const fetchWithdrawRecords = async () => {
    try {
      const resp = await requestMobile('/common/apis/v1/withdraw-log', {
        plat: accountInfo.plat,
        name: accountInfo.remarks_name,
        page_num: 1,
        page_size: 100
      })
      if (resp && !resp.code) {
        setWithdrawRecords(resp.data.data || [])
        setWithdrawRecordsOpen(true)
      }
    } catch (error) {
      console.error('Failed to fetch withdraw records:', error)
    }
  }

  const fetchRechargeRecords = async () => {
    try {
      const resp = await requestMobile('/common/apis/v1/code-flow-list', {
        plat: accountInfo.plat,
        name: accountInfo.remarks_name,
        page_num: 1,
        page_size: 100
      })
      if (resp && !resp.code) {
        setRechargeRecords(resp.data.data || [])
        setRechargeRecordsOpen(true)
      }
    } catch (error) {
      console.error('Failed to fetch recharge records:', error)
    }
  }

  const fetchUsdtRechargeRecords = async (type: 10 | 1) => {
    try {
      const resp = await requestMobile('/common/apis/v1/recharge-log', {
        plat: accountInfo.plat,
        account: accountInfo.remarks_name,
        recharge_type: type,
        // page_num: 1,
        // page_size: 100
      })
      if (resp && !resp.code) {
        setUsdtRechargeRecords(resp.data.data || [])
        setUsdtRechargeType(type)
        setUsdtRechargeRecordsOpen(true)
      }
    } catch (error) {
      console.error('Failed to fetch USDT recharge records:', error)
    }
  }

  // 只加载IP列表，账号详情需要点击刷新按钮
  useEffect(() => {
    if (accountInfo.remarks_name) {
      fetchProxyList()
    }
  }, [accountInfo.remarks_name])

  useEffect(() => {
    if (accountInfo.remarks_name) {
      loadFlowData()
    }
  }, [accountInfo.remarks_name, timeRange])

  useEffect(() => {
    setTaskProcessChecked(false)
    checkTaskProcess()
    const timer = window.setInterval(checkTaskProcess, 10000)
    return () => window.clearInterval(timer)
  }, [accountInfo.task])

  const majorActionsLocked = taskProcessRunning || !taskProcessChecked

  useEffect(() => {
    const viewport = taskLogViewportRef.current
    if (!taskLog || !viewport) return

    const frame = requestAnimationFrame(() => {
      viewport.scrollTop = viewport.scrollHeight
    })

    return () => cancelAnimationFrame(frame)
  }, [taskLog])

  const handleRefresh = () => {
    fetchAccountDetail()
    // fetchProxyList()
    // loadFlowData()
    // loadTaskLog()
  }

  // Task operation handlers
  const handleRestartTask = async () => {
    if (!accountInfo.task || !accountInfo.node) {
      toast.error('任务信息不完整')
      return
    }

    try {
      const resp = await requestMobile('/common/apis/v1/restart_task', {
        task_name: accountInfo.task,
        server_name: accountInfo.node,
      })
      if (resp && resp.code === 0) {
        toast.success('任务重启成功')
      }
    } catch (error) {
      console.error('Failed to restart task:', error)
    }
  }

  const handleStopTask = async () => {
    if (!accountInfo.task || !accountInfo.node) {
      toast.error('任务信息不完整')
      return
    }

    try {
      const resp = await requestMobile('/common/apis/v1/stop_task', {
        task_name: accountInfo.task,
        server_name: accountInfo.node,
      })
      if (resp && resp.code === 0) {
        toast.success('任务停止成功')
        window.setTimeout(checkTaskProcess, 1000)
      }
    } catch (error) {
      console.error('Failed to stop task:', error)
    }
  }

  const handleWithdrawMode = async () => {
    if (!accountInfo.task) {
      toast.error('任务信息不完整')
      return
    }

    try {
      const resp = await requestMobile('/common/apis/v1/set_wd', {
        t_name: accountInfo.task,
        plat: accountInfo.plat,
      })
      if (resp && resp.code === 0) {
        toast.success('已切换到提现模式')
      }
    } catch (error) {
      console.error('Failed to set withdraw mode:', error)
    }
  }

  const handleRunningMode = async () => {
    if (!accountInfo.task) {
      toast.error('任务信息不完整')
      return
    }

    try {
      const resp = await requestMobile('/common/apis/v1/set_run_s', {
        t_name: accountInfo.task,
      })
      if (resp && resp.code === 0) {
        toast.success('已切换到跑分模式')
      }
    } catch (error) {
      console.error('Failed to set running mode:', error)
    }
  }

  const handlePauseTask = async () => {
    if (!accountInfo.task_id) {
      toast.error('任务信息不完整')
      return
    }

    try {
      const resp = await request('/system/api/update/task', {
        id: accountInfo.task_id,
        current_count: -1
      })
      if (resp && resp.code === 0) {
        toast.success('任务已暂停')
      } else {
        toast.error((resp as any)?.msg || '任务暂停失败')
      }
    } catch (error) {
      console.error('Failed to pause task:', error)
      toast.error('任务暂停失败')
    }
  }

  // Account operation handlers
  const handleRecharge = () => {
    setRechargeDialogOpen(true)
  }

  const handleClaimReward = () => {
    setClaimRewardDialogOpen(true)
  }

  const handleClearCode = async () => {
    if (clearingCode) return
    if (taskProcessRunning) {
      toast.error('任务正在运行，请先停止任务')
      setClearCodeConfirmOpen(false)
      return
    }

    setClearingCode(true)
    try {
      const resp = await requestMobile('/common/apis/v1/code-clean', {
        plat: accountInfo.plat,
        name: accountInfo.remarks_name
      })
      if (resp && resp.code === 0) {
        toast.success('清码成功')
        setClearCodeConfirmOpen(false)
        // handleRefresh()
      } else {
        toast.error((resp as any)?.message || '清码失败')
      }
    } catch (error) {
      console.error('Failed to clear code:', error)
      toast.error('清码失败')
    } finally {
      setClearingCode(false)
    }
  }

  const handleBindCard = () => {
    setBindCardDialogOpen(true)
  }

  const handleBindUsdt = () => {
    setBindUsdtDialogOpen(true)
  }

  const handleWithdraw = async () => {
    setCheckingWithdraw(true)
    try {
      const resp = await requestMobile('/common/apis/v1/code-flow-list', {
        plat: accountInfo.plat,
        name: accountInfo.remarks_name,
        page_num: 1,
        page_size: 100,
      })
      if (resp && !resp.code) {
        const records: RechargeRecord[] = resp.data.data || []
        const pending = records.filter((record) => String(record.status) === '0')
        if (pending.length > 0) {
          const pendingIncome = pending.reduce(
            (sum, record) => sum + (Number(record.income) || 0),
            0
          )
          setWithdrawPendingIncome(pendingIncome)
          setWithdrawWarningOpen(true)
          return
        }
        setWithdrawDialogOpen(true)
      } else {
        toast.error((resp as any)?.message || '获取打码记录失败')
      }
    } catch (error) {
      console.error('Failed to fetch code flow list:', error)
      toast.error('获取打码记录失败')
    } finally {
      setCheckingWithdraw(false)
    }
  }

  const handleUpdate = async () => {
    try {
      const resp = await requestMobile('/common/apis/v1/update_user', {
        ...accountInfo
      })
      if (resp && resp.code === 0) {
        toast.success('更新成功')
        // handleRefresh()
      } else {
        toast.error((resp as any)?.message || '更新失败')
      }
    } catch (error) {
      console.error('Failed to update:', error)
      toast.error('更新失败')
    }
  }

  const handleContactService = async () => {
    try {
      const resp = await requestMobile('/common/apis/v1/get_customer_service', {
        plat: accountInfo.plat,
        name: accountInfo.remarks_name
      })
      if (resp && resp.code === 0 && resp.data?.url) {
        window.open(resp.data.url, '_blank')
      } else {
        toast.error('获取客服链接失败')
      }
    } catch (error) {
      console.error('Failed to get customer service:', error)
      toast.error('获取客服链接失败')
    }
  }

  return (
    <div className="min-h-full bg-background">
      {/* 导航栏 */}
      <div className="bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60 border-b" style={{ paddingTop: 'max(env(safe-area-inset-top), 16px)' }}>
        <div className="flex items-center justify-between p-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            返回
          </Button>
          <h1 className="text-lg font-bold">账号详情</h1>
          <div className="w-20"></div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* 基本信息 - 每个字段作为独立的信息块 */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 gap-3">
              {/* name */}
              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">name</p>
                <p className="font-medium">{accountInfo.remarks_name}</p>
              </div>

              {/* plat */}
              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">plat</p>
                <p className="font-medium">{accountInfo.plat}</p>
              </div>

              {/* id */}
              {accountInfo.account_id && (
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">id</p>
                  <p className="font-medium text-sm">{accountInfo.account_id}</p>
                </div>
              )}

              {/* level */}
              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">level</p>
                <p className="font-medium">{accountDetail.level_integral || accountInfo.level_integral || '-'}</p>
              </div>

              {/* vip */}
              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">vip</p>
                <p className="font-medium">{accountDetail.vip || accountInfo.vip || '-'}</p>
              </div>

              {/* 余额 */}
              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">余额</p>
                <p className="font-medium text-green-500">
                  {accountDetail.total_account?.toFixed(2) || accountInfo.total_account?.toFixed(2) || '0.00'}
                </p>
              </div>

              {/* 手机 */}
              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">手机</p>
                <p className="font-medium">{accountDetail.phone || accountInfo.phone || '-'}</p>
              </div>

              {/* 任务 */}
              {accountInfo.task && (
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">任务</p>
                  <p className="font-medium text-sm">{accountInfo.task}</p>
                </div>
              )}

              {/* 节点 */}
              {accountInfo.node && (
                <div className="bg-muted/30 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">节点</p>
                  <p className="font-medium text-sm">{accountInfo.node}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Primary 操作按钮 */}
        <Card>
          <CardContent className="p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-muted-foreground">主要操作</h3>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2"
                onClick={checkTaskProcess}
                disabled={checkingTaskProcess}
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 ${checkingTaskProcess ? 'animate-spin' : ''}`}
                />
                <span className="ml-1 text-xs">检查任务</span>
              </Button>
            </div>
            {!taskProcessChecked && (
              <div className="mb-3 flex items-center gap-2 rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                正在检查任务运行状态，主要操作暂时锁定...
              </div>
            )}
            {taskProcessRunning && (
              <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p className="font-semibold">任务正在运行，主要操作已锁定</p>
                  <p className="mt-1 text-xs">
                    请先在下方“任务控制”中停止任务，停止后再执行充值、清码、提现等操作。
                  </p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <Button onClick={handleRecharge} size="sm" disabled={majorActionsLocked}>
                充值
              </Button>
              <Button onClick={handleClaimReward} size="sm" disabled={majorActionsLocked}>
                领取奖励
              </Button>
              <Button
                onClick={() => setClearCodeConfirmOpen(true)}
                size="sm"
                variant="destructive"
                disabled={clearingCode || majorActionsLocked}
              >
                清码
              </Button>
              <Button onClick={handleBindCard} size="sm" disabled={majorActionsLocked}>
                绑卡
              </Button>
              <Button onClick={handleBindUsdt} size="sm" disabled={majorActionsLocked}>
                绑U
              </Button>
              <Button onClick={handleWithdraw} size="sm" disabled={majorActionsLocked || checkingWithdraw}>
                {checkingWithdraw ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    校验中...
                  </>
                ) : (
                  '提现'
                )}
              </Button>
              <Button onClick={fetchWithdrawRecords} size="sm" disabled={majorActionsLocked}>
                提现记录
              </Button>
              <Button onClick={fetchRechargeRecords} size="sm" disabled={majorActionsLocked}>
                打码记录
              </Button>
              <Button
                onClick={() => fetchUsdtRechargeRecords(10)}
                size="sm"
                disabled={majorActionsLocked}
              >
                线上充值记录
              </Button>
              <Button
                onClick={() => fetchUsdtRechargeRecords(1)}
                size="sm"
                disabled={majorActionsLocked}
              >
                线下充值记录
              </Button>
              <Button
                onClick={handleRefresh}
                size="sm"
                disabled={loading || majorActionsLocked}
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                    刷新
                  </>
                ) : (
                  '刷新'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Outline 辅助按钮 */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold mb-3 text-muted-foreground">辅助功能</h3>
            <div className="grid grid-cols-3 gap-3">
              <Button variant="outline" onClick={handleUpdate} size="sm">
                更新
              </Button>
              <Button variant="outline" onClick={handleContactService} size="sm">
                联系客服
              </Button>
              <Button variant="outline" onClick={fetchProxyList} size="sm">
                刷新IP列表
              </Button>
            </div>

            {/* 任务控制按钮 - 一行两个 */}
            {accountInfo.task && accountInfo.task_id && (
              <div className="grid grid-cols-2 gap-3 mt-3">
                <Button variant="outline" size="sm" onClick={handleRestartTask}>
                  <Play className="h-3 w-3 mr-1" />
                  重启任务
                </Button>
                <Button
                  variant={taskProcessRunning ? 'destructive' : 'outline'}
                  size="sm"
                  onClick={handleStopTask}
                  className={taskProcessRunning ? 'font-semibold' : undefined}
                >
                  <Square className="h-3 w-3 mr-1" />
                  {taskProcessRunning ? '停止任务并解锁' : '停止任务'}
                </Button>
                <Button variant="outline" size="sm" onClick={handlePauseTask}>
                  <Pause className="h-3 w-3 mr-1" />
                  暂停任务
                </Button>
                <Button variant="outline" size="sm" onClick={handleWithdrawMode}>
                  <Coins className="h-3 w-3 mr-1" />
                  提现模式
                </Button>
                <Button variant="outline" size="sm" onClick={handleRunningMode}>
                  <TrendingUp className="h-3 w-3 mr-1" />
                  跑分模式
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* IP列表 - 参考原项目Grid 2列布局，每行显示地址和时间 */}
        {proxyList.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold mb-3">IP列表</h3>
              <div className="grid grid-cols-2 gap-2">
                {proxyList.map((item, index) => (
                  <>
                    <div key={`addr-${index}`} className="text-center text-sm font-mono">
                      {item.address || item.content}
                    </div>
                    <div key={`time-${index}`} className="text-center text-sm text-muted-foreground">
                      {item.update_time}
                    </div>
                  </>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* 任务趋势图表 */}
        <TaskTrendChart
          taskName={accountInfo.remarks_name}
          refreshFlag={loading}
          showTimeSelector={true}
        />

        {/* 认证会话管理 */}
        <AuthSessionPanel accountInfo={accountInfo} />

        {/* 流程数据 */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">流程数据</CardTitle>
                <CardDescription className="text-xs">任务执行流程记录</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Select value={timeRange} onValueChange={setTimeRange}>
                  <SelectTrigger className="w-20 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1h">1小时</SelectItem>
                    <SelectItem value="3h">3小时</SelectItem>
                    <SelectItem value="6h">6小时</SelectItem>
                    <SelectItem value="12h">12小时</SelectItem>
                    <SelectItem value="24h">24小时</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadFlowData}
                  disabled={loadingFlow}
                >
                  <RefreshCw className={`h-3 w-3 ${loadingFlow ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-72">
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">余额</TableHead>
                      <TableHead className="text-xs">下注</TableHead>
                      <TableHead className="text-xs">支付</TableHead>
                      <TableHead className="text-xs">代码</TableHead>
                      <TableHead className="text-xs">时间</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {flowData.length > 0 ? (
                      flowData.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell className="text-xs">{item.balance}</TableCell>
                          <TableCell className="text-xs">{item.bit}</TableCell>
                          <TableCell className="text-xs">{item.current_pay}</TableCell>
                          <TableCell className="text-xs">{item.code}</TableCell>
                          <TableCell className="text-xs whitespace-nowrap">
                            {new Date(item.timestamp).toLocaleString('zh-CN', {
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8 text-xs">
                          {loadingFlow ? '加载中...' : '暂无数据'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="text-xs text-muted-foreground mt-2">共 {flowData.length} 条记录</div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* 任务日志 */}
        {accountInfo.task && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">任务日志</CardTitle>
                  <CardDescription className="text-xs">查看任务运行日志</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">行数:</span>
                    <Input
                      className="w-16 h-8 text-xs"
                      value={logLines}
                      onChange={(e) => setLogLines(e.target.value)}
                      placeholder="100"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={loadTaskLog}
                    disabled={loadingLog}
                  >
                    <RefreshCw className={`h-3 w-3 ${loadingLog ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div
                ref={taskLogViewportRef}
                className="h-72 overflow-y-auto overscroll-contain touch-pan-y border rounded-lg bg-muted/20"
              >
                <div className="p-3 font-mono text-[10px] whitespace-pre-wrap">
                  {taskLog ? (
                    taskLog
                  ) : (
                    <div className="text-center text-muted-foreground py-8 text-xs">
                      {loadingLog ? '加载中...' : '暂无日志'}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <AlertDialog open={clearCodeConfirmOpen} onOpenChange={setClearCodeConfirmOpen}>
        <AlertDialogContent className="w-[calc(100%_-_2rem)] max-w-md rounded-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>确认清码？</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-left">
              <span className="block">清码会修改当前账号的打码数据，请确认操作对象：</span>
              <span className="block rounded-md bg-muted p-3 text-foreground">
                <span className="block">账号：{accountInfo.remarks_name || '-'}</span>
                <span className="block mt-1">平台：{accountInfo.plat || '-'}</span>
              </span>
              <span className="block">此操作提交后不能通过页面撤销。</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={clearingCode}>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault()
                handleClearCode()
              }}
              disabled={clearingCode}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {clearingCode ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  清码中...
                </>
              ) : (
                '确认清码'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={withdrawWarningOpen} onOpenChange={setWithdrawWarningOpen}>
        <AlertDialogContent className="w-[calc(100%_-_2rem)] max-w-md rounded-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>无法提现</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-left">
              <span className="block">
                去运行提现模式，已完成{withdrawPendingIncome}的流水
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setWithdrawWarningOpen(false)}>
              知道了
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 充值记录弹窗 */}
      <Dialog open={rechargeRecordsOpen} onOpenChange={setRechargeRecordsOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>充值记录</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {rechargeRecords.length > 0 ? (
              rechargeRecords.map((record, index) => (
                <Card key={index}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{record.des}</span>
                      <span className="text-sm text-muted-foreground">
                        {record.create_time}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">金额</span>
                      <span className="font-semibold text-green-500">
                        +¥{record.income}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">货币</span>
                      <span>{record.cur}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">状态</span>
                      <span>{record.status}</span>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-8">暂无充值记录</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* 提现记录弹窗 */}
      <Dialog open={withdrawRecordsOpen} onOpenChange={setWithdrawRecordsOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>提现记录</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {withdrawRecords.length > 0 ? (
              withdrawRecords.map((record) => (
                <Card key={record.order_no}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{record.bank_name}</span>
                      {record.remark && (
                        <span className="text-xs px-2 py-0.5 bg-muted rounded">
                          {record.remark}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">金额</span>
                      <span className="font-semibold text-red-500">
                        -¥{record.withdraw_money}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">状态</span>
                      <span>{record.status}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">时间</span>
                      <span className="text-xs">{record.withdraw_time}</span>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-8">暂无提现记录</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* USDT充值记录弹窗 */}
      <Dialog open={usdtRechargeRecordsOpen} onOpenChange={setUsdtRechargeRecordsOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {usdtRechargeType === 10 ? '线上充值记录' : '线下充值记录'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {usdtRechargeRecords.length > 0 ? (
              usdtRechargeRecords.map((record) => (
                <Card key={record.order_no}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{record.bank_name || '未命名'}</span>
                      {record.remark && (
                        <span className="text-xs px-2 py-0.5 bg-muted rounded">
                          {record.remark}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">订单号</span>
                      <span className="text-xs font-mono">{record.order_no}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">金额</span>
                      <span className="font-semibold text-green-500">
                        +¥{record.recharge_money}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">状态</span>
                      <span className={record.status === 1 ? 'text-green-500' : 'text-yellow-500'}>
                        {record.status === 1 ? '成功' : '处理中'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">时间</span>
                      <span className="text-xs">{record.recharge_time}</span>
                    </div>
                    {record.color && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">标识</span>
                        <span className="text-xs">{record.color}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-8">暂无充值记录</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* 充值对话框 */}
      <RechargeDialog
        open={rechargeDialogOpen}
        onOpenChange={setRechargeDialogOpen}
        accountInfo={accountInfo}
        onSuccess={handleRefresh}
      />

      {/* 提现对话框 */}
      <WithdrawDialog
        open={withdrawDialogOpen}
        onOpenChange={setWithdrawDialogOpen}
        accountInfo={accountInfo}
        onSuccess={handleRefresh}
      />

      {/* 绑卡对话框 */}
      <BindCardDialog
        open={bindCardDialogOpen}
        onOpenChange={setBindCardDialogOpen}
        accountInfo={accountInfo}
        onSuccess={handleRefresh}
      />

      {/* 绑U对话框 */}
      <BindUsdtDialog
        open={bindUsdtDialogOpen}
        onOpenChange={setBindUsdtDialogOpen}
        accountInfo={accountInfo}
        onSuccess={handleRefresh}
      />

      {/* 领取奖励对话框 */}
      <ClaimRewardDialog
        open={claimRewardDialogOpen}
        onOpenChange={setClaimRewardDialogOpen}
        accountInfo={accountInfo}
        onSuccess={handleRefresh}
      />
    </div>
  )
}
